import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import axios from 'axios';

const SITE_URL = 'https://www.wentworthhouse.co.uk';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = BACKEND_URL + '/api';

const DEFAULTS = {
  title: 'Wentworth House Hotel | Georgian B&B in Bath, UK | Book Direct & Save',
  description: 'Wentworth House Hotel, Bath - a charming Georgian B&B with 20 rooms, free parking, full English breakfast. 15-min walk to Roman Baths. Book direct for the best rates.',
  keywords: 'Wentworth House Hotel, Bath hotel, Georgian hotel Bath, B&B Bath, bed and breakfast Bath, hotel near Roman Baths, Bath accommodation, hotel with free parking Bath',
  ogImage: '/api/uploads/d6eb7e015bf94972.jpg',
};

// Unique attribute we stamp on scripts we inject so we can identify + clean them up
const MANAGED_ATTR = 'data-wh-seo';

/**
 * Strip outer `<script ...>` / `</script>` wrappers if the user pasted a full
 * `<script>...</script>` block (as analytics vendors usually provide). Returns
 * the inner JS code. Preserves the code untouched if no wrapper is present.
 */
function stripScriptTags(code) {
  if (!code || typeof code !== 'string') return '';
  let out = code.trim();
  // Remove all <script ...> and </script> tokens (handles multiple blocks in one paste).
  out = out.replace(/<script\b[^>]*>/gi, '').replace(/<\/script\s*>/gi, '');
  return out.trim();
}

/**
 * Inject a single inline or external script into <head>. Returns the element
 * so the caller can remove it on cleanup. Scripts injected this way DO execute
 * (React's JSX-rendered inline <script> children do not execute reliably).
 */
function injectScript({ id, src, inline, async: isAsync = true, dataAttrs = {} }) {
  if (typeof document === 'undefined') return null;
  // De-dup by id if provided
  if (id && document.head.querySelector(`script[${MANAGED_ATTR}="${id}"]`)) return null;
  const el = document.createElement('script');
  el.setAttribute(MANAGED_ATTR, id || Math.random().toString(36).slice(2, 10));
  Object.entries(dataAttrs).forEach(([k, v]) => el.setAttribute(k, v));
  if (src) {
    el.src = src;
    el.async = isAsync;
  } else if (inline) {
    el.text = inline;
  }
  document.head.appendChild(el);
  return el;
}

export default function SEOHead({ title, description, keywords, path = '/', ogImage, settings = {}, injectSchema = false }) {
  const [schemaJson, setSchemaJson] = useState(null);

  useEffect(() => {
    if (injectSchema) {
      axios.get(`${API_URL}/schema.json`)
        .then(r => setSchemaJson(JSON.stringify(r.data)))
        .catch(() => {});
    }
  }, [injectSchema]);

  // ── Inject analytics + custom scripts into real <head> on every settings change ──
  useEffect(() => {
    const injected = [];

    // GA4
    if (settings.google_analytics_id) {
      injected.push(injectScript({
        id: 'ga4-loader',
        src: `https://www.googletagmanager.com/gtag/js?id=${settings.google_analytics_id}`,
      }));
      injected.push(injectScript({
        id: 'ga4-config',
        inline: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments)}gtag('js',new Date());gtag('config','${settings.google_analytics_id}');`,
      }));
    }

    // Google Tag Manager
    if (settings.google_tag_manager) {
      injected.push(injectScript({
        id: 'gtm',
        inline: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${settings.google_tag_manager}');`,
      }));
    }

    // Meta Pixel
    if (settings.meta_pixel_id) {
      injected.push(injectScript({
        id: 'meta-pixel',
        inline: `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${settings.meta_pixel_id}');fbq('track','PageView');`,
      }));
    }

    // Custom head scripts list (multi)
    let headScripts = [];
    try {
      if (settings.custom_head_scripts_list) {
        const list = typeof settings.custom_head_scripts_list === 'string'
          ? JSON.parse(settings.custom_head_scripts_list)
          : settings.custom_head_scripts_list;
        headScripts = Array.isArray(list) ? list.filter(s => s.enabled !== false && s.code) : [];
      }
    } catch { /* ignore */ }
    headScripts.forEach((s, i) => {
      injected.push(injectScript({
        id: `custom-head-${i}-${(s.label || '').replace(/\W+/g, '-').slice(0, 20)}`,
        inline: stripScriptTags(s.code),
      }));
    });
    // Legacy single textarea fallback
    if (!headScripts.length && settings.custom_head_scripts) {
      injected.push(injectScript({ id: 'custom-head-legacy', inline: stripScriptTags(settings.custom_head_scripts) }));
    }

    // Custom body scripts list (injected before </body>)
    let bodyScripts = [];
    try {
      if (settings.custom_body_scripts_list) {
        const list = typeof settings.custom_body_scripts_list === 'string'
          ? JSON.parse(settings.custom_body_scripts_list)
          : settings.custom_body_scripts_list;
        bodyScripts = Array.isArray(list) ? list.filter(s => s.enabled !== false && s.code) : [];
      }
    } catch { /* ignore */ }
    const bodyInjected = [];
    bodyScripts.forEach((s, i) => {
      const code = stripScriptTags(s.code);
      if (!code) return;
      const el = document.createElement('script');
      el.setAttribute(MANAGED_ATTR, `custom-body-${i}`);
      el.text = code;
      document.body.appendChild(el);
      bodyInjected.push(el);
    });
    if (!bodyScripts.length && settings.custom_body_scripts) {
      const code = stripScriptTags(settings.custom_body_scripts);
      if (code) {
        const el = document.createElement('script');
        el.setAttribute(MANAGED_ATTR, 'custom-body-legacy');
        el.text = code;
        document.body.appendChild(el);
        bodyInjected.push(el);
      }
    }

    return () => {
      // Clean up on unmount / settings change
      [...injected, ...bodyInjected].forEach(el => { if (el && el.parentNode) el.parentNode.removeChild(el); });
    };
  }, [
    settings.google_analytics_id,
    settings.google_tag_manager,
    settings.meta_pixel_id,
    settings.custom_head_scripts_list,
    settings.custom_body_scripts_list,
    settings.custom_head_scripts,
    settings.custom_body_scripts,
  ]);

  // === META TAG PRIORITY: page-level props > CMS settings > hardcoded defaults ===
  const seoTitle = title || settings.seo_title || DEFAULTS.title;
  const seoDesc = description || settings.seo_description || DEFAULTS.description;
  const seoKeywords = keywords || settings.seo_keywords || DEFAULTS.keywords;
  const canonicalUrl = `${SITE_URL}${path}`;

  // Resolve favicon
  const faviconUrl = settings.favicon
    ? (settings.favicon.startsWith('/api/') ? `${BACKEND_URL}${settings.favicon}` : settings.favicon)
    : '/favicon.svg';

  // Resolve logo / OG image
  const logoUrl = settings.logo
    ? (settings.logo.startsWith('/api/') ? `${BACKEND_URL}${settings.logo}` : settings.logo)
    : null;
  const resolvedOgImage = ogImage
    || logoUrl
    || (DEFAULTS.ogImage.startsWith('/api/') ? `${BACKEND_URL}${DEFAULTS.ogImage}` : DEFAULTS.ogImage);

  return (
    <Helmet>
      {/* === UNIQUE PER-PAGE TITLE & DESCRIPTION === */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDesc} />
      <meta name="keywords" content={seoKeywords} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Favicon */}
      <link rel="icon" href={faviconUrl} />
      <link rel="apple-touch-icon" href={faviconUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDesc} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={resolvedOgImage} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Wentworth House Hotel" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDesc} />
      <meta name="twitter:image" content={resolvedOgImage} />

      {/* Google Search Console Verification */}
      {settings.google_search_console && (
        <meta name="google-site-verification" content={settings.google_search_console} />
      )}

      {/* JSON-LD Structured Data */}
      {schemaJson && (
        <script type="application/ld+json">{schemaJson}</script>
      )}
    </Helmet>
  );
}
